import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { PROFILE_VERSION } from '../lib/gameVersion'
import { invalidatePlayerProfiles } from '../lib/queryClient'

/**
 * Strangers applying to join the guild from the landing page (migration 120).
 * Each application is tied to the Discord account the applicant confirmed;
 * only officers can read them.
 */

export type GuildApplicationStatus = 'pending' | 'approved' | 'declined' | 'spam'

export interface GuildApplication {
  id: string
  created_at: string
  discord_user_id: string
  discord_username: string | null
  discord_avatar_url: string | null
  /** "First Family", as everywhere else on the site */
  character_name: string
  class_name: string
  race: string
  spec_name: string | null
  role_name: string
  os_spec: string | null
  os_role: string | null
  membership_intent: 'social' | 'raider'
  twow_character_name: string | null
  about: string | null
  status: GuildApplicationStatus
  officer_note: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  /** They have since logged in to the site as a member */
  joined: boolean
  /** ...and registered a main for the current era */
  registeredMain: string | null
}

export interface GuildApplicationDraft {
  firstName: string
  familyName: string
  className: string
  race: string
  specName: string
  roleName: string
  osSpec: string
  osRole: string
  membershipIntent: 'social' | 'raider'
  twowCharacterName: string
  about: string
}

export const EMPTY_DRAFT: GuildApplicationDraft = {
  firstName: '',
  familyName: '',
  className: '',
  race: '',
  specName: '',
  roleName: '',
  osSpec: '',
  osRole: '',
  membershipIntent: 'raider',
  twowCharacterName: '',
  about: '',
}

// The form is filled in before the trip to Discord and sent after it, so it
// waits in the tab meanwhile. sessionStorage: nothing outlives the tab.
const DRAFT_KEY = 'raven:applicationDraft'

export function saveApplicationDraft(draft: GuildApplicationDraft) {
  try {
    window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch { /* the applicant fills it in again */ }
}

export function loadApplicationDraft(): GuildApplicationDraft | null {
  try {
    const raw = window.sessionStorage.getItem(DRAFT_KEY)
    // Merged over the empty form, so a draft saved by an older version of
    // the form still has every field
    return raw ? { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<GuildApplicationDraft>) } : null
  } catch {
    return null
  }
}

export function clearApplicationDraft() {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY)
  } catch { /* nothing to clear */ }
}

/** Needs the applicant's Discord session. Returns an error message, or null. */
export async function submitGuildApplication(draft: GuildApplicationDraft): Promise<string | null> {
  const { error } = await supabase.rpc('submit_guild_application', {
    p_first_name: draft.firstName,
    p_family_name: draft.familyName,
    p_class_name: draft.className,
    p_race: draft.race,
    p_spec_name: draft.specName,
    p_role_name: draft.roleName,
    p_os_spec: draft.osSpec,
    p_os_role: draft.osSpec ? draft.osRole : '',
    p_membership_intent: draft.membershipIntent,
    p_twow_character_name: draft.twowCharacterName,
    p_about: draft.about,
  })
  return error?.message ?? null
}

// PostgREST drops over-long .in() URLs silently, so look ids up in chunks
const IN_CHUNK = 300

async function selectByDiscordIds<T>(
  table: 'user_accounts' | 'player_profiles',
  columns: string,
  ids: string[],
  currentMainsOnly = false,
): Promise<T[]> {
  const rows: T[] = []
  for (let i = 0; i < ids.length; i += IN_CHUNK) {
    let query = supabase.from(table).select(columns).in('discord_user_id', ids.slice(i, i + IN_CHUNK))
    if (currentMainsOnly) query = query.eq('game_version', PROFILE_VERSION).eq('is_main', true)
    const { data, error } = await query
    if (error) throw error
    rows.push(...((data ?? []) as unknown as T[]))
  }
  return rows
}

const guildApplicationKeys = {
  all: ['guild_applications', 'all'] as const,
}

/** Officers: every application, newest first, with whether they have joined since */
export function useAllGuildApplications(enabled: boolean) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: guildApplicationKeys.all,
    enabled,
    queryFn: async (): Promise<GuildApplication[]> => {
      const { data, error } = await supabase
        .from('guild_applications')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      const applications = (data ?? []) as Omit<GuildApplication, 'joined' | 'registeredMain'>[]

      // Only the ones still worth following up
      const ids = [...new Set(applications.filter(a => a.status !== 'spam').map(a => a.discord_user_id))]
      const [accounts, profiles] = ids.length
        ? await Promise.all([
            selectByDiscordIds<{ discord_user_id: string }>('user_accounts', 'discord_user_id', ids),
            selectByDiscordIds<{ discord_user_id: string; character_name: string }>(
              'player_profiles', 'discord_user_id, character_name', ids, true,
            ),
          ])
        : [[], []]
      const joined = new Set(accounts.map(a => a.discord_user_id))
      const mains = new Map(profiles.map(p => [p.discord_user_id, p.character_name]))

      return applications.map(a => ({
        ...a,
        joined: joined.has(a.discord_user_id),
        registeredMain: mains.get(a.discord_user_id) ?? null,
      }))
    },
  })

  const review = useCallback(async (
    application: GuildApplication,
    decision: Exclude<GuildApplicationStatus, 'pending'>,
    officerAccountId: string,
    officerNote: string | null,
  ): Promise<string | null> => {
    const { error } = await supabase
      .from('guild_applications')
      .update({
        status: decision,
        officer_note: officerNote,
        reviewed_by: officerAccountId,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', application.id)
    // Approving creates their character in the database (migrations 124,
    // 152), so the roster has a new row to show
    if (!error && decision === 'approved') invalidatePlayerProfiles()
    void queryClient.invalidateQueries({ queryKey: ['guild_applications'] })
    return error?.message ?? null
  }, [queryClient])

  const applications = query.data ?? []
  return {
    pending: applications.filter(a => a.status === 'pending'),
    // Spam stays out of the reviewed list - nobody needs to read it - but is
    // kept reachable, since a spam mark blocks that Discord account for good
    // (migration 123) and a mistaken one has to be undoable
    reviewed: applications.filter(a => a.status === 'approved' || a.status === 'declined'),
    spam: applications.filter(a => a.status === 'spam'),
    loading: query.isPending && enabled,
    review,
  }
}
